import { FC } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Grid,
  Stack,
  TextField,
  useToaster,
} from '@forto-dev/phoenix-ui-core'

import { useAuth } from '@/features/auth'
import { useLocalisation } from '@/utils/localisation'
import { error } from '@/utils/logger'

import { PasswordFormSchema, PasswordFormSchemaType } from './schema'
import { isCurrentPassswordError } from '../api/settings.service'
import { usePasswordUpdate } from '../api/usePasswordUpdate'

export const PasswordSettings: FC = () => {
  const { user } = useAuth()
  const { translate, translateFormValidation, isValidFormTranslationKey } = useLocalisation()
  const toaster = useToaster()
  const passwordMutation = usePasswordUpdate()
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<PasswordFormSchemaType>({
    resolver: zodResolver(PasswordFormSchema),
  })

  const onSubmit: SubmitHandler<PasswordFormSchemaType> = async (data) => {
    if (user) {
      try {
        await passwordMutation.mutateAsync({
          customerId: user.customer_id ?? '?',
          userId: user._id ?? '?',
          data: {
            currentPassword: data.currentPassword,
            password: data.newPassword,
          },
        })
        toaster({
          title: translate('settings.password.notifications.updated'),
        })
      } catch (e) {
        if (isCurrentPassswordError(e)) {
          setError('currentPassword', {
            type: 'manual',
          })
        }
      }
    } else {
      error('User tried to update but has no valid auth object')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card>
        <CardHeader>
          <CardTitle>{translate('settings.password.heading')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Stack gap={16}>
            <TextField
              type="password"
              label={translate('settings.password.fields.current.label')}
              placeholder={translate('settings.password.fields.current.placeholder')}
              required
              {...register('currentPassword')}
              autoComplete="current-password"
              state={errors.currentPassword ? 'negative' : undefined}
            />

            <Grid col={2} gap={16}>
              <TextField
                type="password"
                label={translate('settings.password.fields.new.label')}
                placeholder={translate('settings.password.fields.new.placeholder')}
                required
                {...register('newPassword')}
                autoComplete="new-password"
                state={errors.newPassword ? 'negative' : undefined}
                hint={
                  isValidFormTranslationKey(errors.newPassword?.message)
                    ? translateFormValidation(errors.newPassword.message)
                    : errors.newPassword?.message
                }
              />

              <TextField
                type="password"
                label={translate('settings.password.fields.confirm.label')}
                placeholder={translate('settings.password.fields.confirm.placeholder')}
                required
                {...register('newPassword2')}
                autoComplete="new-password"
                state={errors.newPassword2 ? 'negative' : undefined}
                hint={
                  isValidFormTranslationKey(errors.newPassword2?.message)
                    ? translateFormValidation(errors.newPassword2.message)
                    : errors.newPassword2?.message
                }
              />
            </Grid>
          </Stack>
        </CardContent>
        <CardFooter>
          <Button type="submit">{translate('settings.password.actions.update')}</Button>
        </CardFooter>
      </Card>
    </form>
  )
}
